// ----------------------------------------------------------------------------
// Section: Quiz Flow
// ----------------------------------------------------------------------------
//
// Multi-step quiz state machine:
// - Swaps between step blocks (intro / question / email gate) and drives the
//   shared chrome (back link + gradient progress bar) and loading screen.
// - Tallies archetype scores and builds the structured profile from the
//   data-* attributes on quiz-option blocks.
// - On completion the profile is written to localStorage
//   ("sitka_quiz_<quiz id>"), pushed to the GTM dataLayer, dispatched as a
//   "quiz:completed" document event (consumed by the Quiz Results section)
//   and POSTed to the backend endpoint configured on the section (the URL
//   differs per environment, same pattern as stamped-reviews-summary).
// - Theme editor: selecting a step block jumps the preview to that step.
//
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Styles
import './_quiz-flow.scss';

// -------------------------------------
// Variables
// -------------------------------------

var QUIZ_SECTION = '.section--quiz-flow';
var QUIZ_ROOT = '.js-quiz-flow';
var QUIZ_CONFIG = '.js-quiz-config';
var QUIZ_STEP = '.js-quiz-step';
var QUIZ_OPTION = '.js-quiz-option';
var QUIZ_NEXT = '.js-quiz-next';
var QUIZ_BACK = '.js-quiz-back';
var QUIZ_FORWARD = '.js-quiz-forward';
var QUIZ_REMAINING = '.js-quiz-remaining';
var QUIZ_CONTINUE = '.js-quiz-continue';
var QUIZ_CHROME = '.js-quiz-chrome';
var QUIZ_EYEBROW = '.js-quiz-eyebrow';
var QUIZ_PROGRESS_FILL = '.js-quiz-progress-fill';
var QUIZ_PROGRESS_LABEL = '.js-quiz-progress-label';
var QUIZ_EMAIL_FORM = '.js-quiz-email-form';
var QUIZ_EMAIL_INPUT = '.js-quiz-email-input';
var QUIZ_EMAIL_ERROR = '.js-quiz-email-error';
var QUIZ_SKIP = '.js-quiz-skip';
var QUIZ_LOADING = '.js-quiz-loading';
var QUIZ_LOADING_MESSAGE = '.js-quiz-loading-message';
var ACTIVE_CLASS = 'is-active';
var DISABLED_CLASS = 'is-disabled';
var SELECTED_CLASS = 'is-selected';
var INITIALIZED_CLASS = 'is-initialized';
var COMPLETE_CLASS = 'is-complete';
var AUTO_ADVANCE_DELAY = 260;
var LOADING_MESSAGE_DELAY = 650;
var EMAIL_PROGRESS_PCT = 92;
var EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
var STORAGE_PREFIX = 'sitka_quiz_';

// -------------------------------------
// Helper functions
// -------------------------------------

// Read the section's JSON config script
function readConfig(root) {
  var configEl = root.querySelector(QUIZ_CONFIG);
  var config = {};
  if (configEl) {
    try {
      config = JSON.parse(configEl.textContent);
    } catch (error) {
      config = {};
    }
  }
  config.loadingMessages = (config.loadingMessages || []).map(function (message) {
    return message.trim();
  }).filter(function (message) {
    return message.length > 0;
  });
  config.tieBreakPriority = (config.tieBreakPriority || []).map(function (key) {
    return key.trim();
  }).filter(function (key) {
    return key.length > 0;
  });
  return config;
}

// Push an event to the GTM dataLayer (skipped inside the theme editor so
// merchants clicking through blocks don't pollute analytics)
function trackEvent(payload) {
  if (window.Shopify && window.Shopify.designMode) {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

// POST the completed submission to the configured backend endpoint.
// Fire-and-forget: the submit must never block or delay the result reveal,
// so failures stay silent (the profile is still in localStorage/dataLayer).
function submitProfile(url, submission) {
  if (!url || window.Shopify && window.Shopify.designMode) {
    return;
  }
  try {
    window.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(submission),
      keepalive: true
    }).catch(function () {});
  } catch (error) {
    // fetch unavailable (legacy browser) — capture stays local-only
  }
}

// -------------------------------------
// Quiz instance
// -------------------------------------

function createQuiz(sectionEl) {
  var root = sectionEl.querySelector(QUIZ_ROOT);
  if (!root || root.classList.contains(INITIALIZED_CLASS)) {
    return;
  }
  root.classList.add(INITIALIZED_CLASS);
  var config = readConfig(root);
  var quizId = root.dataset.quizId || 'quiz';
  var steps = Array.prototype.slice.call(root.querySelectorAll(QUIZ_STEP));
  var questions = steps.filter(function (step) {
    return step.dataset.stepType === 'question';
  });
  var chrome = root.querySelector(QUIZ_CHROME);
  var backButton = root.querySelector(QUIZ_BACK);
  var forwardButton = root.querySelector(QUIZ_FORWARD);
  var remainingLabel = root.querySelector(QUIZ_REMAINING);
  var progressFill = root.querySelector(QUIZ_PROGRESS_FILL);
  var progressLabel = root.querySelector(QUIZ_PROGRESS_LABEL);
  var loading = root.querySelector(QUIZ_LOADING);
  var loadingMessage = root.querySelector(QUIZ_LOADING_MESSAGE);
  var currentIndex = 0;
  var email = null;
  var completed = false;
  var hasNavigated = false;
  var advanceTimer = null;
  if (steps.length === 0) {
    return;
  }

  // ---- Step display ----

  function showStep(index) {
    // A stale auto-advance timer must never fire across a navigation
    // (double-clicking an answer would otherwise skip the next step —
    // including the email gate on the last question)
    window.clearTimeout(advanceTimer);
    currentIndex = Math.max(0, Math.min(index, steps.length - 1));
    steps.forEach(function (step, i) {
      step.classList.toggle(ACTIVE_CLASS, i === currentIndex);
    });
    updateChrome();

    // The theme header can push a newly shown step below the fold; skip
    // the initial render so page load doesn't hijack the scroll position
    if (hasNavigated) {
      sectionEl.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    hasNavigated = true;
    trackEvent({
      event: 'quiz_step_viewed',
      quizId: quizId,
      stepIndex: currentIndex,
      stepType: steps[currentIndex].dataset.stepType
    });
  }
  function updateChrome() {
    var step = steps[currentIndex];
    var type = step.dataset.stepType;
    if (!chrome) {
      return;
    }
    if (type === 'intro') {
      chrome.hidden = true;
      return;
    }
    chrome.hidden = false;
    if (type === 'question') {
      var questionIndex = questions.indexOf(step);
      if (backButton) {
        backButton.hidden = questionIndex === 0;
      }
      if (forwardButton) {
        forwardButton.hidden = false;
        // Forward never advances past an unanswered question; the
        // disabled look tells the visitor an answer is required
        forwardButton.classList.toggle(DISABLED_CLASS, !isStepAnswered(step));
        forwardButton.setAttribute('aria-disabled', String(!isStepAnswered(step)));
      }
      if (remainingLabel) {
        var remaining = questions.length - questionIndex - 1;
        remainingLabel.hidden = remaining === 0;
        remainingLabel.textContent = remaining + ' more';
      }
      if (progressFill) {
        progressFill.style.width = questionIndex / questions.length * 100 + '%';
      }
      if (progressLabel) {
        progressLabel.textContent = '';
      }
      var eyebrow = step.querySelector(QUIZ_EYEBROW);
      if (eyebrow && eyebrow.dataset.autoEyebrow) {
        eyebrow.textContent = 'Question ' + (questionIndex + 1);
      }
    } else if (type === 'email') {
      if (backButton) {
        backButton.hidden = true;
      }
      if (forwardButton) {
        forwardButton.hidden = true;
      }
      if (remainingLabel) {
        remainingLabel.hidden = true;
      }
      if (progressFill) {
        progressFill.style.width = EMAIL_PROGRESS_PCT + '%';
      }
      if (progressLabel) {
        progressLabel.textContent = step.dataset.progressLabel || '';
      }
    }
  }
  function goToNextStep() {
    if (currentIndex < steps.length - 1) {
      showStep(currentIndex + 1);
    } else {
      finish();
    }
  }
  function goToPreviousStep() {
    if (currentIndex > 0) {
      showStep(currentIndex - 1);
    }
  }

  // ---- Answers, scoring and profile ----

  function selectedOptions() {
    return Array.prototype.slice.call(root.querySelectorAll(QUIZ_OPTION + '.' + SELECTED_CLASS));
  }
  function isStepAnswered(step) {
    return !!step.querySelector(QUIZ_OPTION + '.' + SELECTED_CLASS);
  }
  function buildProfile() {
    var profile = {
      source: quizId
    };
    var scores = {};
    selectedOptions().forEach(function (option) {
      var key = option.dataset.fieldKey;
      var value = option.dataset.fieldValue;
      if (key && value) {
        // Multi-select questions accumulate comma-separated values
        profile[key] = profile[key] ? profile[key] + ',' + value : value;
      }
      [['archetypePrimary', 'archetypePrimaryWeight'], ['archetypeSecondary', 'archetypeSecondaryWeight']].forEach(function (pair) {
        var archetype = option.dataset[pair[0]];
        var weight = parseInt(option.dataset[pair[1]], 10) || 0;
        if (archetype) {
          scores[archetype] = (scores[archetype] || 0) + weight;
        }
      });
    });
    profile.archetype = winningArchetype(scores);
    return profile;
  }
  function winningArchetype(scores) {
    var keys = Object.keys(scores);
    if (keys.length === 0) {
      return null;
    }

    // Order candidates by the configured tie-break priority; unknown keys
    // keep their insertion order after the prioritized ones.
    var ordered = config.tieBreakPriority.filter(function (key) {
      return keys.indexOf(key) !== -1;
    }).concat(keys.filter(function (key) {
      return config.tieBreakPriority.indexOf(key) === -1;
    }));
    var best = ordered[0];
    ordered.forEach(function (key) {
      if (scores[key] > scores[best]) {
        best = key;
      }
    });
    return best;
  }

  // ---- Completion ----

  function finish() {
    if (completed) {
      return;
    }
    completed = true;
    if (config.loadingEnabled && loading) {
      showLoading();
    } else {
      complete();
    }
  }
  function showLoading() {
    steps.forEach(function (step) {
      step.classList.remove(ACTIVE_CLASS);
    });
    if (chrome) {
      chrome.hidden = true;
    }
    loading.hidden = false;
    var messages = config.loadingMessages;
    var messageIndex = 0;
    if (loadingMessage && messages.length > 0) {
      loadingMessage.textContent = messages[0];
    }
    var rotation = window.setInterval(function () {
      if (loadingMessage && messages.length > 0) {
        messageIndex = (messageIndex + 1) % messages.length;
        loadingMessage.textContent = messages[messageIndex];
      }
    }, LOADING_MESSAGE_DELAY);
    window.setTimeout(function () {
      window.clearInterval(rotation);
      loading.hidden = true;
      complete();
    }, config.loadingDuration || 2300);
  }
  function complete() {
    var profile = buildProfile();
    if (email) {
      profile.email = email;
    }
    profile.completedAt = new Date().toISOString();
    try {
      window.localStorage.setItem(STORAGE_PREFIX + quizId, JSON.stringify(profile));
    } catch (error) {
      // Storage unavailable (private mode) — the event below still fires
    }
    trackEvent({
      event: 'quiz_completed',
      quizId: quizId,
      quizProfile: profile
    });
    document.dispatchEvent(new CustomEvent('quiz:completed', {
      detail: profile
    }));

    // Skipped emails are submitted too (profile.email absent) — the
    // backend decides what to do with anonymous submissions. The
    // session customer id travels outside the profile as a separate
    // identity signal; the typed email is the one that associates.
    submitProfile(config.submitUrl, {
      quizId: quizId,
      profile: profile,
      sessionCustomerId: root.dataset.customerId || null
    });
    sectionEl.classList.add(COMPLETE_CLASS);
  }

  // ---- Event wiring ----

  root.addEventListener('click', function (event) {
    var option = event.target.closest(QUIZ_OPTION);
    if (option) {
      handleOptionClick(option);
      return;
    }
    if (event.target.closest(QUIZ_NEXT)) {
      trackEvent({
        event: 'quiz_started',
        quizId: quizId
      });
      goToNextStep();
      return;
    }
    if (event.target.closest(QUIZ_BACK)) {
      goToPreviousStep();
      return;
    }

    // Forward and Continue share the same guard: an unanswered question
    // can never be advanced past
    if (event.target.closest(QUIZ_FORWARD) || event.target.closest(QUIZ_CONTINUE)) {
      if (isStepAnswered(steps[currentIndex])) {
        goToNextStep();
      }
      return;
    }
    if (event.target.closest(QUIZ_SKIP)) {
      email = null;
      finish();
    }
  });
  function handleOptionClick(option) {
    var step = option.closest(QUIZ_STEP);
    var isMulti = step.dataset.multiSelect === 'true';
    if (isMulti) {
      option.classList.toggle(SELECTED_CLASS);
      updateChrome();
      return;
    }
    Array.prototype.forEach.call(step.querySelectorAll(QUIZ_OPTION), function (other) {
      other.classList.remove(SELECTED_CLASS);
    });
    option.classList.add(SELECTED_CLASS);
    updateChrome();
    window.clearTimeout(advanceTimer);
    advanceTimer = window.setTimeout(goToNextStep, AUTO_ADVANCE_DELAY);
  }
  var emailForm = root.querySelector(QUIZ_EMAIL_FORM);
  if (emailForm) {
    emailForm.addEventListener('submit', function (event) {
      event.preventDefault();
      var input = emailForm.querySelector(QUIZ_EMAIL_INPUT);
      var error = emailForm.querySelector(QUIZ_EMAIL_ERROR);
      var value = input ? input.value.trim() : '';
      if (!EMAIL_PATTERN.test(value)) {
        if (error) {
          error.textContent = error.dataset.message || 'Please enter a valid email.';
        }
        return;
      }
      if (error) {
        error.textContent = '';
      }
      email = value;
      finish();
    });
  }

  // ---- Theme editor support ----

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:block:select', function (event) {
      if (!sectionEl.contains(event.target)) {
        return;
      }
      var step = event.target.closest(QUIZ_STEP) || event.target.querySelector(QUIZ_STEP);
      if (step) {
        var index = steps.indexOf(step);
        if (index !== -1) {
          showStep(index);
        }
      }
    });
  }
  showStep(0);
}

// -------------------------------------
// Init
// -------------------------------------

function initQuizzes() {
  document.querySelectorAll(QUIZ_SECTION).forEach(function (sectionEl) {
    createQuiz(sectionEl);
  });
}
document.addEventListener('DOMContentLoaded', initQuizzes);

// Re-init after the theme editor re-renders the section
document.addEventListener('shopify:section:load', function (event) {
  if (event.target.querySelector && event.target.querySelector(QUIZ_ROOT)) {
    initQuizzes();
  }
});